import { Dispatch, MutableRefObject } from 'react';
import { toast } from 'react-toastify';

import mapboxgl from 'mapbox-gl';

import MapboxLanguage from '@mapbox/mapbox-gl-language';
import * as turf from '@turf/turf';

import { ProjectInfo } from 'state/userSlice';

import { MapStyle } from 'appConstants';
import { showToast } from 'features/common/toasts/ToastProvider';
import { Feature, MultiPolygon, Polygon } from 'geojson';

import { LayerRouting, PtLatLng, RouteInfo, Segment } from '../../../appTypes';
import {
  kDefaultMapStyle,
  setMapCenter,
  setMapZoom,
  undoRouteSelection,
} from '../../../state/workflowSlice';
import {
  BasicRouteInfo,
  routeSelectionIsSavedRoute,
} from '../../common/hooks/useRouteInfo';
import { deepEquals, isCtrlEvent } from '../../common/utils';
import { filterIdToRoadClass } from '../tooltips/RoadClassTooltip/utils';
import {
  bboxFromPointBuffer,
  findNearestSegmentLatLng,
  geojsonCoordinates,
  geojsonToCoordinates,
  mergeSegmentsBySegmentIds,
} from './geography_utils';
import { MapControl } from './mapControls';
import {
  getBookmarkLayerDefinition,
  getHoveredSegmentLayerArrowDefinition,
  getHoveredSegmentLayerDefinition,
  getProjectMaskLayerDefinition,
  getSelectedRouteLayerDefinition,
  getSelectedSegmentLayerArrowDefinition,
  getSelectedSegmentLayerDefinition,
  getSubscriptionBoundaryFadeLayerDefinition,
  getSubscriptionBoundaryLineLayerDefinition,
  kProjectBoundaryLayerDefinition,
} from './mapLayerDefinitions';
import {
  addMapLayerInStack,
  findNearestSegmentOnMap,
  getGeoJSONLayerData,
  makeSnakeFromIDs,
  searchGeoJSONLayerForID,
  setLayerVisibility,
} from './mapUtils';
import {
  MAP_COLORED_ICONS_TO_LOAD,
  MAP_ICONS_TO_LOAD,
  MAP_STYLE_LOOKUP,
  MAX_ZOOM,
  MIN_MAP_ZOOM,
  MIN_ZOOM,
  initZoom,
  kBookmarkLayerId,
  kHoveredSegmentLayerId,
  kHoveredSegmentSourceId,
  kHoveredSegmentSymbolsLayerId,
  kMainVisLineOpacity,
  kMainVisLineOpacityExpression,
  kMainVisLineOpacitySegmentSelectedExpression,
  kMapLayerStack,
  kMaxScreenDistanceForRouteSelect,
  kMaxScreenDistanceForSelect,
  kMonitorRouteBorderLayerId,
  kProjectBoundaryLayerId,
  kProjectMaskLayerId,
  kRoadClassControlId,
  kRouteArrowsBgLayerId,
  kRouteArrowsLayerId,
  kSegmentLayerId,
  kSegmentLayerSourceLayerId,
  kSegmentSourceId,
  kSegmentSymbolsLayerId,
  kSelectedRouteLayerId,
  kSelectedSegmentLayerId,
  kSelectedSegmentSymbolsLayerId,
  kSettingsControlId,
  kSubscriptionBoundaryFadeLayerId,
  kSubscriptionBoundaryLineLayerId,
  kSubscriptionBoundarySourceId,
  kViewOnlySegmentLayerId,
} from './map_constants';
import { getRouteCost, getRoutingForSegmentPair } from './route_utils';

function mapStyleToUrl(mapStyle) {
  return MAP_STYLE_LOOKUP[mapStyle || kDefaultMapStyle];
}

function loadUsedImages(map) {
  function loadImageWhenReady(name: any, element: any, sdf: boolean = false) {
    const options: any = {};
    if (sdf) {
      options.sdf = true;
    }
    if (element.complete) {
      if (map.hasImage(name)) map.removeImage(name);
      map.addImage(name, element, options);
    } else {
      element.onload = () => {
        if (map.hasImage(name)) map.removeImage(name);
        map.addImage(name, element, options);
      };
    }
  }
  MAP_ICONS_TO_LOAD.map((item) => {
    const el = document.querySelector(`#${item}`) as any;
    loadImageWhenReady(item, el, true);
    return item;
  });
  MAP_COLORED_ICONS_TO_LOAD.map((item) => {
    const el = document.querySelector(`#${item}`) as any;
    loadImageWhenReady(item, el, false);
    return item;
  });
}

function getProjectCenter(userProject: ProjectInfo | undefined) {
  if (!userProject) {
    return { center: undefined, zoom: undefined };
  }

  const initCenter = geojsonToCoordinates(userProject?.project_center);
  return { center: initCenter, zoom: initZoom };
}

export function recenterMapToProjectBbox(dispatch, userProject) {
  const { center: projectCenter, zoom: projectZoom } =
    getProjectCenter(userProject);
  dispatch(setMapZoom(projectZoom));
  dispatch(setMapCenter(projectCenter));
}

type MapHandlers = {
  handleMapLoadCallback?: () => void;
  handleMapMoveCallback?: () => void;
  handleSegmentClickCallback?: () => void;
  handleMapSettingsClickCallback?: () => void;
  handleRoadClassFilterClickCallback?: (e: any) => void;
};

export function initMap(
  refMap: MutableRefObject<mapboxgl.Map>,
  refMapContainer: MutableRefObject<HTMLElement>,
  center: { lat: number; lng: number },
  zoom: number,
  mapStyle: string,
  showChartSettings: boolean,
  handlers: MapHandlers = {},
  dispatch?: Dispatch<any>,
  userProject?: ProjectInfo,
  allowLowZooms = false,
  hideControls = false,
  showRoadClassFilter = true,
) {
  const {
    handleMapLoadCallback = () => {},
    handleMapMoveCallback = () => {},
    handleSegmentClickCallback = () => {},
    handleMapSettingsClickCallback = () => {},
    handleRoadClassFilterClickCallback = (e: any) => {},
  } = handlers;

  const style_url = mapStyleToUrl(mapStyle);
  const map = new mapboxgl.Map({
    container: refMapContainer.current,
    style: style_url,
    center: [center.lng, center.lat],
    zoom,
    minZoom: allowLowZooms ? 1 : MIN_ZOOM,
    maxZoom: MAX_ZOOM,
    attributionControl: false,
    preserveDrawingBuffer: true,
  });
  refMap.current = map;
  // map.showTileBoundaries = true;
  map.dragRotate.disable();
  map.touchZoomRotate.disableRotation();
  map.addControl(new mapboxgl.AttributionControl(), 'top-right');

  map.addControl(
    new mapboxgl.NavigationControl({
      showCompass: false,
    }),
  );

  map.addControl(
    new MapboxLanguage({
      defaultLanguage: 'mul',
    }),
  );
  if (showChartSettings && !hideControls) {
    map.addControl(
      new MapControl(
        kSettingsControlId,
        'Map settings',
        false,
        handleMapSettingsClickCallback,
      ),
    );
  }
  if (showRoadClassFilter)
    map.addControl(
      new MapControl(
        kRoadClassControlId,
        'Filter road classes',
        false,
        handleRoadClassFilterClickCallback,
      ),
    );
  if (userProject && !hideControls) {
    refMap.current.addControl(
      new MapControl('map-recenter', 'Recenter map', true, () => {
        recenterMapToProjectBbox(dispatch, userProject);
      }),
    );
  }

  loadUsedImages(map);
  map.on('load', handleMapLoadCallback);
  map.on('moveend', handleMapMoveCallback);
  map.on('click', handleSegmentClickCallback);

  // document.querySelector<HTMLElement>('#chart-showhide').onclick = handleChartShowHideClickCallback;
}

export function mapHasLayer(map: mapboxgl.Map, layerId) {
  if (!map) return false;
  return map.getLayer(layerId) !== undefined;
}

export function mapLayerSafeRemove(map, layerId) {
  if (mapHasLayer(map, layerId)) {
    map.removeLayer(layerId);
  }
}

export function mapSourceSafeRemove(map, sourceId) {
  if (map.getSource(sourceId) !== undefined) {
    map.removeSource(sourceId);
  } else {
    console.info('Source does not exist', sourceId);
  }
}

export function mapHasSource(map, sourceId) {
  if (map.getSource(sourceId)) return true;
  return false;
}

export function mapShowingSelectedSegment(
  map: mapboxgl.Map,
  checkLoaded = false,
) {
  return (
    mapHasLayer(map, kSelectedSegmentLayerId) &&
    (!checkLoaded || (map.getSource(kSelectedSegmentLayerId) as any)._loaded) // eslint-disable-line
  );
}

export function mapShowingSegments(map) {
  return mapHasLayer(map, kSegmentLayerId);
}

export function mapShowingRouteSegments(map) {
  return mapHasLayer(map, kSelectedRouteLayerId);
}

export function addMapLayerInDefinedStack(
  map,
  layerDefinition: mapboxgl.AnyLayer,
) {
  return addMapLayerInStack(map, layerDefinition, kMapLayerStack);
}

export function mapPushSelectedSegment(
  map: mapboxgl.Map,
  mapStyle: MapStyle,
  segment,
  dimRemainingSegments: boolean = true,
) {
  // console.assert(!mapShowingSelectedSegment(map));
  if (!mapShowingSelectedSegment(map)) {
    // console.log('Drawing selected segment', segment);
    map.addSource(kSelectedSegmentLayerId, {
      type: 'geojson',
      data: segment,
    });
    addMapLayerInDefinedStack(map, getSelectedSegmentLayerDefinition(mapStyle));
    addMapLayerInDefinedStack(
      map,
      getSelectedSegmentLayerArrowDefinition(mapStyle),
    );
    if (mapShowingSegments(map) && dimRemainingSegments) {
      map.setPaintProperty(
        kSegmentLayerId,
        'line-opacity',
        kMainVisLineOpacitySegmentSelectedExpression,
      );
    }
    console.assert(mapShowingSelectedSegment(map));
  }
}

export function mapPushRouteSelect(route, map, mapStyle: MapStyle) {
  // eslint-disable-next-line
  if (!mapShowingRouteSegments(map) && map.style._loaded) {
    if (route) {
      map.addSource(kSelectedRouteLayerId, {
        type: 'geojson',
        data: route,
      });
      addMapLayerInDefinedStack(map, getSelectedRouteLayerDefinition(mapStyle));
    }
  }
}

export function mapPopSelectedSegment(map, undimOtherSegments = true) {
  console.assert(mapShowingSelectedSegment(map));
  mapLayerSafeRemove(map, kSelectedSegmentLayerId);
  mapLayerSafeRemove(map, kSelectedSegmentSymbolsLayerId);
  mapLayerSafeRemove(map, kMonitorRouteBorderLayerId);
  mapLayerSafeRemove(map, kRouteArrowsLayerId);
  mapLayerSafeRemove(map, kRouteArrowsBgLayerId);
  mapSourceSafeRemove(map, kSelectedSegmentLayerId);
  if (mapHasLayer(map, kSegmentLayerId) && undimOtherSegments) {
    map.setPaintProperty(
      kSegmentLayerId,
      'line-opacity',
      kMainVisLineOpacityExpression(kMainVisLineOpacity),
    );
  }
  console.assert(!mapShowingSelectedSegment(map));
}

export function mapPopRouteSegments(map) {
  mapLayerSafeRemove(map, kSelectedRouteLayerId);
  mapSourceSafeRemove(map, kSelectedRouteLayerId);
}

export function mapPushVectorSegments(
  map: mapboxgl.Map,
  sourceId: string,
  url_subdirectory: string,
  layerDefinitions: Object[],
  min_zoom: number,
  max_zoom: number,
  redrawEvenIfSourceIsUnchanged = false,
  sharedFilter?: mapboxgl.Expression,
): boolean {
  const url = `${window.location.protocol}//${window.location.hostname}:${window.location.port}/${url_subdirectory}/{z}/{x}/{y}/tile.bin`;
  // Prevent redraws by checking if the url will change or not
  const oldSource = map.getSource(sourceId);
  if (
    oldSource &&
    // eslint-disable-next-line
    (oldSource as any)._options.tiles[0] === url &&
    !redrawEvenIfSourceIsUnchanged
  ) {
    console.debug('Source is identical, not redrawing');
    return false;
  }

  // Redraw

  layerDefinitions.forEach((layerDefinition) => {
    mapLayerSafeRemove(map, (layerDefinition as any).id);
  });

  mapLayerSafeRemove(map, kSegmentSymbolsLayerId);
  mapLayerSafeRemove(map, kViewOnlySegmentLayerId);

  mapSourceSafeRemove(map, sourceId);
  map.addSource(sourceId, {
    type: 'vector',
    tiles: [url],
    minzoom: min_zoom,
    maxzoom: max_zoom,
    promoteId: 'base_segment_id',
  });

  layerDefinitions.forEach((layerDefinition) => {
    const withFilter =
      sharedFilter != null
        ? { ...layerDefinition, filter: sharedFilter }
        : layerDefinition;
    addMapLayerInDefinedStack(map, {
      ...withFilter,
      'source-layer': kSegmentLayerSourceLayerId,
    } as mapboxgl.AnyLayer);
  });
  return true;
}

export function searchMapForSegmentId(
  map: mapboxgl.Map,
  segment_ids: string[],
  layerId = undefined,
) {
  let finalLayerId = layerId;
  if (!layerId) {
    finalLayerId = kSegmentSourceId;
  }
  const params = {
    filter: ['in', ['get', 'segment_id'], ['literal', segment_ids]],
    validate: false,
  };
  if (!layerId || layerId === kSegmentSourceId) {
    (params as any).sourceLayer = kSegmentLayerSourceLayerId;
  }

  const segments = map.querySourceFeatures(finalLayerId, params);
  return segments;
}

/**
 * Returns all features with matching segment_ids from a GeoJSON source,
 * regardless of viewport visibility. Unlike querySourceFeatures (searchMapForSegmentId), this reads
 * the source'ss underlying data directly.
 */
export function getAllFeaturesFromGeoJSONSource(
  map: mapboxgl.Map,
  sourceId: string,
  segment_ids: string[],
): GeoJSON.Feature[] {
  const source = map.getSource(sourceId) as mapboxgl.GeoJSONSource & {
    _data?: GeoJSON.FeatureCollection;
  };
  if (!source) return [];
  const selectedIds = new Set(segment_ids);
  return ((source._data as GeoJSON.FeatureCollection)?.features ?? []).filter(
    (f) => selectedIds.has(f.properties?.segment_id),
  );
}

export function redrawBaseSegments(
  mapReady: boolean,
  map: mapboxgl.Map,
  layerDefinitions: Object[],
  url_subdirectory: string,
  min_zoom = MIN_MAP_ZOOM,
  max_zoom = 16,
  redrawEvenIfSourceIsUnchanged = false,
): boolean {
  if (!mapReady) {
    return false;
  }

  if (mapShowingSelectedSegment(map)) {
    mapPopSelectedSegment(map);
  }

  if (url_subdirectory) {
    return mapPushVectorSegments(
      map,
      kSegmentSourceId,
      url_subdirectory,
      layerDefinitions,
      min_zoom,
      max_zoom,
      redrawEvenIfSourceIsUnchanged,
    );
  }
  return false;
}

export function getRoadClassLayerFilter(
  roadFilters: string[] | null | undefined,
) {
  if (!roadFilters) return undefined; // case: all- filter untouched

  const roadClasses = roadFilters.map((el) => filterIdToRoadClass(el));
  return ['all', ['in', ['get', 'hwc'], ['literal', roadClasses]]];
}

export function mergeMapLayerFilters(...parts: any[]): any {
  const normalized = parts.filter(Array.isArray);
  if (normalized.length === 0) return undefined;
  if (normalized.length === 1) return normalized[0];
  return ['all', ...normalized];
}

export function getDrawableMapSegments(map, segmentIds, layer_id, force) {
  const segDrawn = searchGeoJSONLayerForID(map, layer_id, segmentIds);
  const mapSegment = mergeSegmentsBySegmentIds(
    searchMapForSegmentId(map, segmentIds),
  );
  if (
    // Have we not already drawn the segment?
    !segDrawn || // Have we not already drawn this ID?
    force || // Are we forcing a redraw?
    (mapSegment &&
      !deepEquals(geojsonCoordinates(segDrawn), geojsonCoordinates(mapSegment))) // Is the old segment out of date because new tiles have loaded?
  ) {
    if (mapSegment) {
      return { needsRedraw: true, drawable: mapSegment };
    }
  }
  return { needsRedraw: false, drawable: undefined };
}

export function redrawSelectedSegment(
  map: mapboxgl.Map,
  mapStyle: MapStyle,
  selectedSegmentId: string | undefined,
  selectedRoute: string[] | undefined,
  routeFeature: RouteInfo | undefined,
  basicRouteInfo: BasicRouteInfo | undefined,
  force = false,
  dimOtherSegsOnSelection: boolean = false,
) {
  if (selectedSegmentId) {
    const { needsRedraw, drawable } = getDrawableMapSegments(
      map,
      [selectedSegmentId],
      kSelectedSegmentLayerId,
      force,
    );
    if (needsRedraw) {
      if (mapShowingSelectedSegment(map)) {
        mapPopSelectedSegment(map, false); // No need to undim as we're going to restyle next anyway
      }
      mapPushSelectedSegment(map, mapStyle, drawable, dimOtherSegsOnSelection);
    }
  } else if (mapShowingSelectedSegment(map)) {
    mapPopSelectedSegment(map, dimOtherSegsOnSelection);
  }
  if (routeFeature) {
    if (mapShowingRouteSegments(map)) {
      mapPopRouteSegments(map);
    }
    mapPushRouteSelect(routeFeature, map, mapStyle);
  } else if (
    basicRouteInfo &&
    selectedRoute &&
    !routeSelectionIsSavedRoute(selectedRoute)
  ) {
    const routeSegmentIds = basicRouteInfo.realizedSegments;
    const selectedRouteString = selectedRoute.join(',');
    const newRouteGeom = makeSnakeFromIDs(
      selectedRouteString,
      routeSegmentIds,
      map,
    );
    let needsRedraw = false;
    if (mapShowingRouteSegments(map)) {
      const existingRoute = getGeoJSONLayerData(map, kSelectedRouteLayerId);
      // Check if we've previously actually drawn all the route segments
      // Or if this try at a re-draw gets us more segments - in which case we want to draw it!
      // This is because, depending on zoom and tile loads, we don't neccsiarily have all of the
      // segments already on the map to query
      let needsPop = false;
      const drawn_ids = existingRoute?.properties?.segment_ids;
      const new_draw_ids = newRouteGeom?.properties?.segment_ids;
      const drawn_ids_length = (drawn_ids || '').split(',').length;
      const new_draw_ids_length = (new_draw_ids || '').split(',').length;
      if (!existingRoute || force) {
        needsPop = true;
      } else if (
        !drawn_ids ||
        drawn_ids_length < new_draw_ids_length || // Only redraw if there are more segments available
        (drawn_ids_length === new_draw_ids_length &&
          drawn_ids !== new_draw_ids) || // Or if they're the same number but a different group
        existingRoute?.properties?.selected_route !== selectedRouteString // Or its drawing an old route
      ) {
        needsPop = true;
      }
      if (needsPop) {
        mapPopRouteSegments(map);
        needsRedraw = true;
      }
    } else {
      needsRedraw = true;
    }
    if (needsRedraw) {
      mapPushRouteSelect(newRouteGeom, map, mapStyle);
    }
  } else if (mapShowingRouteSegments(map)) {
    mapPopRouteSegments(map);
  }
  // else if (!needsCrosswalk(layer, selectedSegmentId, undefined)) {
  //   // e.g. previously selected segment not in this layer
  //   console.log('Selected segment doesn\'t exist and doesn\'t need a crosswalk, deselecting');
  //   dispatch(setSelectedSegmentId(undefined));
  // }
}

export function getNearestSegmentFromMouseEvent(
  map,
  event,
  layerId,
  buffer = 8,
) {
  const features = map.queryRenderedFeatures(
    bboxFromPointBuffer(event.point, buffer),
    { layers: [layerId] },
  );
  if (features.length > 0) {
    const { nearestSegment } = findNearestSegmentLatLng(
      features as any,
      event.lngLat,
    );
    return nearestSegment;
  }
  return undefined;
}

export function getRouteInfoFromMouseEvent(map, event, layerId, buffer = 8) {
  const features = map.queryRenderedFeatures(
    bboxFromPointBuffer(event.point, buffer),
    { layers: [layerId] },
  );
  if (features.length > 0) return features[0];
  return undefined;
}

/**
 *
 * @param event the click event, which includes the lnglat of the new map click
 * @param map
 * @param refRouting the routing map, a dictionary
 * @param routeSelectionEnabled
 * @param isCtrlClick
 * @param selectedSegment the most recently selected segment
 * @param selectedRoute all the currently selected segments
 * @returns the routing map and the click, and routes
 */
export function getSelectionFromClickEvent(
  event,
  map,
  refRouting,
  routeSelectionEnabled,
  isCtrlClick,
  selectedSegment,
  selectedRoute,
  filter = undefined,
  lookForExistingRoutesOnLayer = undefined,
  onUndo?: () => void,
) {
  let returnedRoute;
  const maxSelectDistance = isCtrlClick
    ? kMaxScreenDistanceForRouteSelect
    : kMaxScreenDistanceForSelect;
  const clickBbox = bboxFromPointBuffer(event.point, maxSelectDistance);
  const segments = map.queryRenderedFeatures(clickBbox, {
    layers: [kSegmentLayerId],
    filter,
  });

  if (
    lookForExistingRoutesOnLayer &&
    mapHasLayer(map, lookForExistingRoutesOnLayer)
  ) {
    const routes = map.queryRenderedFeatures(clickBbox, {
      layers: [lookForExistingRoutesOnLayer],
    });
    // console.log('Looking for existing routes', routes);
    if (!isCtrlClick && routes.length > 0) {
      let nearestRoute;
      try {
        nearestRoute = findNearestSegmentOnMap(
          routes,
          event.lngLat,
          maxSelectDistance,
          map,
          selectedRoute,
        ).nearestSegment;
      } catch (e) {
        // Fallback for multi line routes
        nearestRoute = routes[0]; // eslint-disable-line prefer-destructuring
      }
      return {
        segmentId: undefined,
        segment: undefined,
        route: nearestRoute.properties.route,
      };
    }
  }

  const { nearestSegment, usedExclude: isSecondClick } =
    findNearestSegmentOnMap(
      segments,
      event.lngLat,
      maxSelectDistance,
      map,
      selectedSegment,
    );
  // console.log(`### selected segment: ${JSON.stringify(nearestSegment)}`);

  // remove existing selection from map (if any)

  // set new nearestSegmentId (or undefined if none) in DOM ref and in global persisted state
  const nearestSegmentId =
    nearestSegment && nearestSegment.properties.segment_id;
  let routingFailed = false;
  // console.log('Selecting ', nearestSegmentId);
  if (isCtrlClick && routeSelectionEnabled) {
    if (nearestSegmentId && refRouting.current) {
      let previouslySelectedSegment = selectedSegment;
      let selectedRouteLocal = [
        ...(selectedRoute || [previouslySelectedSegment]),
      ];
      if (isSecondClick && selectedRouteLocal.length > 1) {
        // If we're switching to the second-closest segment, reroute rather than add more route
        // Reverse engineer whether it was prepended or appended
        const rindex = selectedRouteLocal.indexOf(previouslySelectedSegment);
        // console.debug('Popping previous segment due to second-click', rindex);
        if (rindex === 0) {
          selectedRouteLocal = selectedRouteLocal.slice(
            1,
            selectedRouteLocal.length - 1,
          );
          // eslint-disable-next-line
          previouslySelectedSegment = selectedRouteLocal[0];
        } else if (rindex === selectedRouteLocal.length - 1) {
          selectedRouteLocal.pop();
          previouslySelectedSegment =
            selectedRouteLocal[selectedRouteLocal.length - 1];
        }
      }
      // console.log(previouslySelectedSegment, 'to', nearestSegmentId);

      // Try forward routing
      const fwRouting = getRoutingForSegmentPair(
        refRouting.current as LayerRouting,
        previouslySelectedSegment,
        nearestSegmentId,
      );
      const fwCost = getRouteCost(
        refRouting.current as LayerRouting,
        fwRouting,
      );
      // Try reverse routing
      const bwRouting = getRoutingForSegmentPair(
        refRouting.current as LayerRouting,
        nearestSegmentId,
        previouslySelectedSegment,
      );
      const bwCost = getRouteCost(
        refRouting.current as LayerRouting,
        bwRouting,
      );

      let useReverse = false;
      if (bwCost < fwCost || (bwCost && !fwCost)) {
        useReverse = true;
        console.debug('Detected reverse routing!');
      }

      const routing = useReverse ? bwRouting : fwRouting;

      // console.debug('route Selection', selectedRouteLocal, 'new routing', routing);
      const newSparseRoute = [...selectedRouteLocal];
      if (routing) {
        if (!useReverse) {
          newSparseRoute.push(nearestSegmentId);
        } else {
          // console.debug('Inserting new selection to start of route, pre=', newSparseRoute);
          newSparseRoute.unshift(nearestSegmentId);
          // console.debug('Post: ', newSparseRoute);
        }
        toast.dismiss();
      } else {
        routingFailed = true;
        if (selectedRouteLocal.length === 0) {
          selectedRouteLocal.splice(0, selectedRouteLocal.length);
        }
        showToast('warning', "Can't find routing between selected segments", {
          name: 'Undo last selection',
          fnc: onUndo,
        });
      }
      console.debug('New sparse route', newSparseRoute);
      returnedRoute = newSparseRoute.filter((v) => v); // Filter out nulls and undefined
    } else {
      returnedRoute = null;
    }
  } else {
    returnedRoute = null;
  }
  return {
    segmentId: nearestSegmentId,
    segment: nearestSegment,
    route: returnedRoute,
    routingFailed,
  };
}

export function getInitialCenter(
  userProject: ProjectInfo | undefined,
  mapCenter?: PtLatLng | null,
  mapZoom?: number | null,
) {
  if (userProject || mapCenter) {
    const initCenter = geojsonToCoordinates(userProject?.project_center);
    const center = mapCenter || initCenter;
    const zoom = mapZoom ?? initZoom;
    return { center, zoom };
  }
  return { center: undefined, zoom: undefined };
}

export function safeRemoveBookmarks(map) {
  mapLayerSafeRemove(map, kBookmarkLayerId);
  mapSourceSafeRemove(map, kBookmarkLayerId);
}

export function showBookmarks(
  map: mapboxgl.Map,
  bookmarks,
  mapStyle: MapStyle,
) {
  if (mapHasLayer(map, kBookmarkLayerId)) {
    safeRemoveBookmarks(map);
  }
  map.addSource(kBookmarkLayerId, {
    type: 'geojson',
    data: bookmarks,
  });
  addMapLayerInDefinedStack(map, getBookmarkLayerDefinition(mapStyle));
}

export function updateMapLocation(
  map?: mapboxgl.Map,
  mapZoom?: number,
  mapCenter?: PtLatLng,
) {
  if (map) {
    const mapSet: mapboxgl.CameraOptions = {};
    if (mapCenter) {
      // Move margin is 1/5th of a tile
      // At zoom 14 this is 0.004 degrees
      const moveMargin = 360 / (5 * 2 ** mapZoom); // Degrees
      const center = {
        lng: map.getCenter().lng,
        lat: map.getCenter().lat,
      };
      if (
        Math.abs(center.lng - mapCenter.lng) > moveMargin ||
        Math.abs(center.lat - mapCenter.lat) > moveMargin
      ) {
        mapSet.center = mapCenter;
      }
    }
    if (mapZoom) {
      const zoomMargin = 0.1;
      const currentZoom = map.getZoom();
      if (Math.abs(currentZoom - mapZoom) > zoomMargin) {
        mapSet.zoom = mapZoom;
      }
    }
    if (Object.keys(mapSet).length > 0) {
      map.jumpTo(mapSet);
    }
  }
}
// works for Windows and macOS
export function isUndo(e: KeyboardEvent) {
  return isCtrlEvent(e) && e.key === 'z';
}

export function handleWindowKeyDownRouteSelectionUndo(
  e,
  dispatch,
  routeSelectionEnabled,
) {
  if (routeSelectionEnabled && isUndo(e) && e.target.nodeName !== 'INPUT') {
    dispatch(undoRouteSelection());
    e.preventDefault();
  }
}

export function addOrUpdateMapHoveredSegment(
  map: mapboxgl.Map,
  mapStyle: MapStyle,
  hoveredSegment: Segment | undefined,
  hoveredSegmentLayerDefinition: mapboxgl.LineLayer = undefined,
  hoveredSegmentLayerArrowDefinition: mapboxgl.SymbolLayer = undefined,
) {
  // console.log('Redraw hover');
  let hasLayer = mapHasLayer(map, kHoveredSegmentLayerId);
  if (!hasLayer && hoveredSegment) {
    map.addSource(kHoveredSegmentSourceId, {
      type: 'geojson',
      data: hoveredSegment,
    });
    if (!hoveredSegmentLayerDefinition) {
      hoveredSegmentLayerDefinition =
        getHoveredSegmentLayerDefinition(mapStyle);
    }
    if (!hoveredSegmentLayerArrowDefinition) {
      hoveredSegmentLayerArrowDefinition =
        getHoveredSegmentLayerArrowDefinition(mapStyle);
    }

    console.assert(hoveredSegmentLayerDefinition !== undefined);
    addMapLayerInDefinedStack(map, hoveredSegmentLayerDefinition);
    addMapLayerInDefinedStack(map, hoveredSegmentLayerArrowDefinition);
    hasLayer = true;
  }
  if (hasLayer) {
    // We could set data to an empty feature collection, but for some reason
    // this causes very long garbage collection cycles. Hiding the layer when
    // there is nothing to show reduces this problem
    if (hoveredSegment) {
      (map.getSource(kHoveredSegmentSourceId) as any).setData(hoveredSegment);
    }
    setLayerVisibility(!!hoveredSegment, kHoveredSegmentLayerId, map);
    setLayerVisibility(!!hoveredSegment, kHoveredSegmentSymbolsLayerId, map);
  }
}

export function mapPulseAnimation(map: mapboxgl.Map, layerId: string) {
  let opacity = 0.6;
  let direction = 1;
  let cancelled = false;

  const animate = () => {
    if (cancelled || !map) return;
    try {
      opacity += direction * 0.015;

      if (opacity <= 0.2) {
        opacity = 0.2;
        direction = 1;
      } else if (opacity >= 1) {
        opacity = 1;
        direction = -1;
      }

      if (mapHasLayer(map, layerId)) {
        map.setPaintProperty(layerId, 'line-opacity', [
          'case',
          ['boolean', ['get', 'isHovered'], false],
          opacity,
          0,
        ]);
      }
    } catch (err) {
      console.error('mapPulseAnimation halted:', err);
      return;
    }

    requestAnimationFrame(animate);
  };
  if (!cancelled) {
    animate();
  }

  return () => {
    cancelled = true; // cleanup
  };
}

export function setHoverCursorTypeForHover(
  map: mapboxgl.Map,
  isHovered: boolean,
) {
  if (isHovered) {
    map.getCanvas().style.cursor = 'pointer';
  } else {
    map.getCanvas().style.cursor = '';
  }
}

function inverseBoundary(boundary: Feature<MultiPolygon | Polygon>) {
  const inverse = turf.difference(
    turf.featureCollection([
      turf.polygon([
        [
          [-180, 90],
          [180, 90],
          [180, -90],
          [-180, -90],
          [-180, 90],
        ],
      ]),
      boundary,
    ]),
  );

  return inverse;
}

export function drawSubscriptionBoundary(
  map: mapboxgl.Map,
  mapStyle: MapStyle,
  subscriptionBoundary: Feature<MultiPolygon | Polygon>,
) {
  mapLayerSafeRemove(map, kSubscriptionBoundaryFadeLayerId);
  mapLayerSafeRemove(map, kSubscriptionBoundaryLineLayerId);
  mapSourceSafeRemove(map, kSubscriptionBoundarySourceId);
  if (subscriptionBoundary) {
    map.addSource(kSubscriptionBoundarySourceId, {
      type: 'geojson',
      data: inverseBoundary(subscriptionBoundary),
    });
    addMapLayerInDefinedStack(
      map,
      getSubscriptionBoundaryFadeLayerDefinition(mapStyle),
    );
    addMapLayerInDefinedStack(
      map,
      getSubscriptionBoundaryLineLayerDefinition(mapStyle),
    );
  }
}

export function mapDrawProjectBoundary(
  map: mapboxgl.Map,
  mapStyle: MapStyle,
  boundary: Polygon,
) {
  mapLayerSafeRemove(map, kProjectBoundaryLayerId);
  mapLayerSafeRemove(map, kProjectMaskLayerId);
  mapSourceSafeRemove(map, kProjectBoundaryLayerId);
  mapSourceSafeRemove(map, kProjectMaskLayerId);
  map.addSource(kProjectBoundaryLayerId, {
    type: 'geojson',
    data: boundary,
  });
  addMapLayerInDefinedStack(map, kProjectBoundaryLayerDefinition);

  map.addSource(kProjectMaskLayerId, {
    type: 'geojson',
    data: inverseBoundary(turf.feature(boundary)),
  });
  addMapLayerInDefinedStack(map, getProjectMaskLayerDefinition(mapStyle));
}

export const setVisIfNeeded = (
  map: mapboxgl.Map,
  visibility: 'none' | 'visible',
  layerId: string,
) => {
  if (!mapHasLayer(map, layerId)) return;
  const current = map.getLayoutProperty(layerId, 'visibility');
  if (current !== visibility) {
    map.setLayoutProperty(layerId, 'visibility', visibility);
  }
};
